import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, TouchableWithoutFeedback, Animated } from 'react-native';
import { DbSwitchProps } from './DbSwitch.types';
import { styles, defaultColors, defaultSizes } from './DbSwitch.styles';

/**
 * DbSwitch 开关组件
 * 
 * 用于切换开/关状态
 * 
 * @example
 * // 非受控模式
 * <DbSwitch defaultValue={true} onChange={(val) => console.log(val)} />
 * 
 * @example
 * // 受控模式
 * <DbSwitch value={isOn} onChange={setIsOn} />
 * 
 * @example
 * // 自定义颜色
 * <DbSwitch activeColor="#34C759" inactiveColor="#CCCCCC" />
 * 
 * @example
 * // 禁用状态
 * <DbSwitch value={true} disabled />
 */
export const DbSwitch: React.FC<DbSwitchProps> = ({
    value,
    defaultValue = false,
    onChange,
    disabled = false,
    activeColor = defaultColors.active,
    inactiveColor = defaultColors.inactive,
    thumbColor = defaultColors.thumb,
    width = defaultSizes.width,
    height = defaultSizes.height,
    thumbSize = defaultSizes.thumbSize,
    thumbMargin = defaultSizes.thumbMargin,
    style,
    thumbStyle,
}) => {
    // 判断是否为受控模式
    const isControlled = value !== undefined;
    
    // 内部状态（非受控模式使用）
    const [internalValue, setInternalValue] = useState(defaultValue);
    
    // 当前实际值
    const currentValue = isControlled ? value : internalValue;
    
    // 动画值
    const animatedValue = useRef(new Animated.Value(currentValue ? 1 : 0)).current;
    
    // 计算滑块移动距离
    const thumbTranslateX = width - thumbSize - thumbMargin * 2;
    
    // 同步动画值
    useEffect(() => {
        Animated.timing(animatedValue, {
            toValue: currentValue ? 1 : 0,
            duration: 200,
            useNativeDriver: false,
        }).start();
    }, [currentValue, animatedValue]);
    
    // 处理点击
    const handlePress = useCallback(() => {
        if (disabled) return;
        
        const newValue = !currentValue;
        
        if (!isControlled) {
            setInternalValue(newValue);
        }
        
        onChange?.(newValue);
    }, [disabled, currentValue, isControlled, onChange]);
    
    // 计算背景颜色插值
    const backgroundColor = animatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [inactiveColor, activeColor],
    });
    
    // 计算滑块位置插值
    const thumbTransform = animatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [thumbMargin, thumbMargin + thumbTranslateX],
    });
    
    return (
        <TouchableWithoutFeedback onPress={handlePress} disabled={disabled}>
            <View style={style}>
                <Animated.View
                    style={[
                        styles.container,
                        {
                            width,
                            height,
                            borderRadius: height / 2,
                            backgroundColor,
                            opacity: disabled ? 0.4 : 1,
                        },
                    ]}
                >
                    <Animated.View
                        style={[
                            styles.thumb,
                            {
                                width: thumbSize,
                                height: thumbSize,
                                borderRadius: thumbSize / 2,
                                backgroundColor: thumbColor,
                                transform: [{ translateX: thumbTransform }],
                            },
                            thumbStyle,
                        ]}
                    />
                </Animated.View>
            </View>
        </TouchableWithoutFeedback>
    );
};

// 保持向后兼容的别名
export const Switch = DbSwitch;

export default DbSwitch;
export type { DbSwitchProps } from './DbSwitch.types';
